/**
 * Adapter hook to convert ControllerInput dep to ControllerInputContextValue interface
 * for use with game-ui components that expect the old interface.
 * Returns a reactive adapter that updates when the signal changes.
 */
import { useMemo } from 'react'
import type { ControllerInput } from '../controllerInput/types'
import type { ControllerInputContextValue, ControllerInputEvent as GameControllerInputEvent } from '@merels/components/hooks'

export function useControllerInputAdapter(
  controllerInput: ControllerInput | null
): ControllerInputContextValue | null {
  return useMemo(() => {
    if (!controllerInput) return null

    return {
      subscribe(handler) {
        return controllerInput.subscribe((event) => {
          if (event.type === 'navigate' || event.type === 'select' || event.type === 'cancel') {
            handler(event as GameControllerInputEvent)
          }
        })
      },
      subscribeHeld(handler) {
        return controllerInput.subscribeHeld(handler)
      },
      // Always "active" from the perspective of game-ui components.
      //
      // Our `controllerInput.isActive` now means "has any controller/keyboard event been emitted yet?",
      // and game-ui uses `isActive` to decide whether to subscribe at all. If we pass `false` here,
      // the first navigation keypress after enabling focus-navigation gets dropped (subscription only
      // attaches after that first event flips the signal).
      //
      // Gating is handled by each component's `enableFocusNavigation` prop instead.
      isActive: true,
    }
  }, [controllerInput])
}
